import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import type { Contact } from '../types/types';

interface EditContactModalProps {
  show: boolean;
  onHide: () => void;
  contact?: Contact; // optional now
  loading?: boolean;
  onSave: (updatedContact: Contact) => void;
  stackedOverModal?: boolean;
}

const EditContactModal: React.FC<EditContactModalProps> = ({
  show,
  onHide,
  contact,
  loading = false,
  onSave,
  stackedOverModal = false,
}) => {
  const [editedContact, setEditedContact] = useState<Contact>(
    contact ?? { id: '', name: '', email: '', phone: '' }, // fallback for "create"
  );
  const [errors, setErrors] = useState<{ [key: string]: string | undefined }>(
    {},
  );

  useEffect(() => {
    setEditedContact(contact ?? { id: '', name: '', email: '', phone: '' }); // reset when modal opens
    setErrors({});
  }, [contact, show]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditedContact((prev) => ({ ...prev, [name]: value }));
    if (value.trim() !== '') {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!editedContact?.name?.trim()) newErrors.name = 'Name is required';
    if (!editedContact?.email?.trim()) newErrors.email = 'Email is required';
    if (
      editedContact?.email &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editedContact.email)
    ) {
      newErrors.email = 'Invalid email format';
    }
    if (!editedContact?.phone?.trim()) newErrors.phone = 'Phone is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validate()) {
      onSave(editedContact);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      className={stackedOverModal ? 'stacked-child-modal' : undefined}
      backdropClassName={
        stackedOverModal ? 'stacked-child-modal-backdrop' : undefined
      }
    >
      <Modal.Header closeButton>
        <Modal.Title>{contact ? 'Edit Contact' : 'Add Contact'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3 position-relative">
            <Form.Label>Name</Form.Label>
            <Form.Control
              type="text"
              name="name"
              value={editedContact?.name || ''}
              onChange={handleChange}
              isInvalid={!!errors.name}
            />
            <Form.Control.Feedback tooltip type="invalid">
              {errors.name}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3 position-relative">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              name="email"
              value={editedContact?.email || ''}
              onChange={handleChange}
              isInvalid={!!errors.email}
            />
            <Form.Control.Feedback tooltip type="invalid">
              {errors.email}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3 position-relative">
            <Form.Label>Phone</Form.Label>
            <Form.Control
              type="text"
              name="phone"
              value={editedContact?.phone || ''}
              onChange={handleChange}
              isInvalid={!!errors.phone}
            />
            <Form.Control.Feedback tooltip type="invalid">
              {errors.phone}
            </Form.Control.Feedback>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={loading}>
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditContactModal;
